/**
 * `/gallery/matchup-pregame` exists so
 * `tests/e2e/src/app/matchupPregameGeometry.e2e.ts` can measure ISSUE-988's
 * pre-game Matchup in a real browser: no sideways scroll and no clipped leader
 * stat at 390px, two columns side by side at 1440px. It is signed in and
 * linked from no navigation, following the `/gallery/week-strip` precedent.
 *
 * The fixtures are a worst case written as literals: six column football
 * leader categories with long values, long stat labels with tied ranks, and
 * a full head to head and last five. No fetch.
 *
 * The file is named `gallery_.matchup-pregame` so this route stays out of
 * `/gallery`'s tree: `/gallery` renders no Outlet, so a nested route would
 * never paint.
 */
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import { MatchupTab } from "../features/gameDetail/MatchupTab";
import type {
  typeFormResult,
  typeLeaderCategory,
  typeMatchupSection,
  typeStatComparisonRow,
} from "../features/gameDetail/matchupTypes";
import type { typeEventOdds } from "../features/gameDetail/oddsLogic";

const TB = {
  teamId: "gallery-tb",
  name: "Tampa Bay Buccaneers",
  abbreviation: "TB",
  logoUrl: null,
};
const JAX = {
  teamId: "gallery-jax",
  name: "Jacksonville Jaguars",
  abbreviation: "JAX",
  logoUrl: null,
};
const NYY = {
  teamId: "gallery-nyy",
  name: "New York Yankees",
  abbreviation: "NYY",
  logoUrl: null,
};
const LAD = {
  teamId: "gallery-lad",
  name: "Los Angeles Dodgers",
  abbreviation: "LAD",
  logoUrl: null,
};

const NFL_ODDS: typeEventOdds = {
  spread: { favoriteTeamId: TB.teamId, points: -3.5, isLocked: false },
  moneyline: null,
  total: null,
};

const leaderCategory = ({
  key,
  title,
  columns,
  values,
  awayName,
  homeName,
}: {
  key: string;
  title: string;
  columns: string[];
  values: string[];
  awayName: string;
  homeName: string;
}): typeLeaderCategory => ({
  key,
  title,
  columns,
  away: { name: awayName, jerseyNumber: "15", headshotUrl: null, values },
  home: { name: homeName, jerseyNumber: "88", headshotUrl: null, values },
});

const statRow = (
  key: string,
  label: string,
  leader: "away" | "home" | "even",
): typeStatComparisonRow => ({
  key,
  label,
  awayValue: "412.3",
  homeValue: "388.9",
  awaySharePercent: 44,
  leader,
  awayRankLabel: "T13th",
  homeRankLabel: "1st",
});

const formResults = (prefix: string): typeFormResult[] => [
  {
    key: `${prefix}-1`,
    resultCode: "W",
    opponentLabel: "vs JAX",
    scoreLabel: "24-17",
    dateLabel: "Sep 13, 2026",
  },
  {
    key: `${prefix}-2`,
    resultCode: "L",
    opponentLabel: "at SEA",
    scoreLabel: "10-31",
    dateLabel: "Sep 6, 2026",
  },
  {
    key: `${prefix}-3`,
    resultCode: "T",
    opponentLabel: "vs LAR",
    scoreLabel: "20-20",
    dateLabel: "Jan 4, 2026",
  },
  {
    key: `${prefix}-4`,
    resultCode: "W",
    opponentLabel: "at ARI",
    scoreLabel: "38-35",
    dateLabel: "Dec 28, 2025",
  },
  {
    key: `${prefix}-5`,
    resultCode: "W",
    opponentLabel: "vs TEN",
    scoreLabel: "27-3",
    dateLabel: "Dec 21, 2025",
  },
];

const NFL_SECTIONS: readonly typeMatchupSection[] = [
  {
    key: "seasonLeaders",
    title: "Season leaders",
    kind: "leaders",
    rows: [],
    leaders: {
      categories: [
        leaderCategory({
          key: "passing",
          title: "Passing",
          columns: ["C/ATT", "YDS", "TD", "INT", "YPG", "RTG"],
          values: ["301/450", "3,587", "27", "14", "239.1", "104.2"],
          awayName: "Christopher Montgomery-Williams",
          homeName: "Trevor Lawrence",
        }),
        leaderCategory({
          key: "rushing",
          title: "Rushing",
          columns: ["CAR", "YDS", "AVG", "TD", "LNG", "YPG"],
          values: ["312", "1,459", "4.7", "14", "72", "97.3"],
          awayName: "Christian McCaffrey",
          homeName: "Travis Etienne Jr.",
        }),
        leaderCategory({
          key: "receiving",
          title: "Receiving",
          columns: ["REC", "YDS", "AVG", "TD", "LNG", "YPG"],
          values: ["104", "1,342", "12.9", "11", "66", "89.5"],
          awayName: "Brandon Aiyuk",
          homeName: "Brian Thomas Jr.",
        }),
      ],
    },
  },
  {
    key: "seasonStats.offense",
    title: "Offense (Per Game)",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("points", "Points", "away"),
        statRow("totalYards", "Total Yards", "home"),
        statRow("passingYards", "Passing Yards", "away"),
        statRow("rushingYards", "Rushing Yards", "even"),
        statRow("firstDowns", "First Downs", "home"),
        statRow("thirdDown", "Third Down Conversion Pct", "away"),
      ],
    },
  },
  {
    key: "seasonStats.defense",
    title: "Defense (Per Game)",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("pointsAllowed", "Points Allowed", "home"),
        statRow("yardsAllowed", "Yards Allowed", "away"),
        statRow("passingYardsAllowed", "Passing Yards Allowed", "home"),
        statRow("rushingYardsAllowed", "Rushing Yards Allowed", "away"),
        statRow("sacks", "Sacks", "even"),
        statRow("takeaways", "Takeaways", "home"),
      ],
    },
  },
  {
    key: "teamComparison",
    title: "Team comparison",
    kind: "rows",
    rows: [
      {
        key: "record",
        label: "Record",
        awayValue: "12-5-0",
        homeValue: "4-13-0",
      },
      {
        key: "awayHome",
        label: "Away / Home",
        awayValue: "6-3-0",
        homeValue: "3-5-0",
      },
      {
        key: "conference",
        label: "Conference",
        awayValue: "8-4-0",
        homeValue: "2-10-0",
      },
      { key: "streak", label: "Streak", awayValue: "W10", homeValue: "L4" },
    ],
  },
  {
    key: "headToHead",
    title: "Head to head",
    kind: "headToHead",
    rows: [],
    headToHead: {
      meetings: [
        ["Nov 10, 2025", "JAX", 17, "TB", 24, "home"],
        ["Oct 22, 2023", "TB", 10, "JAX", 34, "home"],
        ["Nov 14, 2021", "TB", 30, "JAX", 10, "away"],
        ["Dec 24, 2017", "JAX", 33, "TB", 44, "home"],
        ["Oct 26, 2013", "TB", 42, "JAX", 10, "away"],
      ].map(
        (
          [
            dateLabel,
            awayAbbreviation,
            awayScore,
            homeAbbreviation,
            homeScore,
            winnerSide,
          ],
          index,
        ) => ({
          key: `meeting-${index}`,
          dateLabel: String(dateLabel),
          awayAbbreviation: String(awayAbbreviation),
          awayScore: Number(awayScore),
          homeAbbreviation: String(homeAbbreviation),
          homeScore: Number(homeScore),
          winnerSide: winnerSide as "away" | "home",
        }),
      ),
    },
  },
  {
    key: "lastFive",
    title: "Last 5 games",
    kind: "lastFive",
    rows: [],
    lastFive: { away: formResults("sf"), home: formResults("jax") },
  },
  {
    key: "gameDetails",
    title: "Game details",
    kind: "details",
    rows: [],
    details: {
      rows: [
        {
          key: "venue",
          label: "Venue",
          value: "Levi's Stadium, Santa Clara, USA",
        },
        { key: "broadcast", label: "TV", value: "FOX" },
      ],
    },
  },
];

const MLB_SECTIONS: readonly typeMatchupSection[] = [
  {
    key: "seasonLeaders",
    title: "Season leaders",
    kind: "leaders",
    rows: [],
    leaders: {
      categories: [
        leaderCategory({
          key: "pitching",
          title: "Starting Pitching",
          columns: ["W-L", "ERA", "IP", "SO", "WHIP"],
          values: ["12-7", "3.21", "131.2", "158", "1.08"],
          awayName: "Gerrit Cole",
          homeName: "Yoshinobu Yamamoto",
        }),
        leaderCategory({
          key: "batting",
          title: "Batting",
          columns: ["AVG", "HR", "RBI", "OPS"],
          values: [".311", "41", "109", "1.012"],
          awayName: "Aaron Judge",
          homeName: "Shohei Ohtani",
        }),
      ],
    },
  },
  {
    key: "seasonStats.batting",
    title: "Batting",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("runs", "Runs", "away"),
        statRow("avg", "Batting Average", "home"),
        statRow("homeRuns", "Home Runs", "away"),
      ],
    },
  },
  {
    key: "seasonStats.pitching",
    title: "Pitching",
    kind: "statComparison",
    rows: [],
    statComparison: {
      rows: [
        statRow("era", "Earned Run Average", "home"),
        statRow("strikeouts", "Strikeouts", "away"),
      ],
    },
  },
  {
    key: "teamComparison",
    title: "Team comparison",
    kind: "rows",
    rows: [
      {
        key: "record",
        label: "Record",
        awayValue: "94-68",
        homeValue: "98-64",
      },
      {
        key: "awayHome",
        label: "Away / Home",
        awayValue: "44-37",
        homeValue: "52-29",
      },
      { key: "streak", label: "Streak", awayValue: "W3", homeValue: "L1" },
    ],
  },
  {
    key: "gameDetails",
    title: "Game details",
    kind: "details",
    rows: [],
    details: {
      rows: [
        { key: "venue", label: "Venue", value: "Dodger Stadium" },
        { key: "broadcast", label: "TV", value: "FOX, SportsNet LA, YES" },
      ],
    },
  },
];

const MatchupPregameGalleryScreen = (): ReactNode => (
  <div className="flex flex-col gap-6 px-page-gutter py-4">
    <h1 className="text-h1 text-ink">Pre-game matchup gallery</h1>
    <p className="text-body text-ink-secondary">
      Two pre-game Matchup tabs filled with long names and wide stat rows. This
      page exists so a browser can measure the layout on a phone and on a wide
      screen. It is not linked from any navigation.
    </p>

    <div
      data-testid="matchup-pregame-gallery-wide"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">NFL, wide</h2>
      <MatchupTab
        sections={NFL_SECTIONS}
        odds={NFL_ODDS}
        away={TB}
        home={JAX}
        isWide
      />
    </div>

    <div
      data-testid="matchup-pregame-gallery-narrow"
      className="flex flex-col gap-2"
    >
      <h2 className="text-h2 text-ink">MLB, narrow</h2>
      <MatchupTab
        sections={MLB_SECTIONS}
        odds={null}
        away={NYY}
        home={LAD}
        isWide={false}
      />
    </div>
  </div>
);

export const Route = createFileRoute("/gallery_/matchup-pregame")({
  beforeLoad: requireSignedIn,
  component: MatchupPregameGalleryScreen,
});
